const COMPARE_STORAGE_KEY = 'aeromotive:compare';
const COMPARE_EVENT = 'compare:change';

const CompareStore = {
  read() {
    try {
      const raw = window.localStorage.getItem(COMPARE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  },

  write(items) {
    try {
      window.localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
    }
    document.dispatchEvent(new CustomEvent(COMPARE_EVENT, { detail: { items } }));
  },

  has(handle) {
    return this.read().some((item) => item.handle === handle);
  },

  add(item, limit) {
    const items = this.read();
    if (items.some((entry) => entry.handle === item.handle)) return items;
    if (items.length >= limit) return null;
    items.push(item);
    this.write(items);
    return items;
  },

  remove(handle) {
    const items = this.read().filter((item) => item.handle !== handle);
    this.write(items);
    return items;
  },
};

if (!customElements.get('compare-bar')) {
  customElements.define(
    'compare-bar',
    class CompareBar extends HTMLElement {
      connectedCallback() {
        this.slots = this.querySelector('[data-compare-slots]');
        this.status = this.querySelector('[data-compare-status]');
        this.cta = this.querySelector('[data-compare-cta]');
        this.slotTemplate = this.querySelector('[data-compare-slot-template]');
        this.emptyTemplate = this.querySelector('[data-compare-empty-template]');
        this.collapse = this.querySelector('[data-compare-collapse]');

        this.onStoreChange = () => this.render();
        document.addEventListener(COMPARE_EVENT, this.onStoreChange);

        this.onStorage = (event) => {
          if (event.key === COMPARE_STORAGE_KEY) this.render();
        };
        window.addEventListener('storage', this.onStorage);

        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.onMediaChange = () => this.render();
        this.mobileQuery.addEventListener('change', this.onMediaChange);

        this.collapse.addEventListener('click', () => {
          const expanded = this.collapse.getAttribute('aria-expanded') === 'true';
          this.collapse.setAttribute('aria-expanded', String(!expanded));
          this.slots.hidden = expanded;
        });

        this.slots.addEventListener('change', (event) => {
          const input = event.target.closest('[data-compare-slot-remove]');
          if (!input) return;
          const slot = input.closest('[data-compare-slot]');
          CompareStore.remove(slot.dataset.handle);
        });

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(COMPARE_EVENT, this.onStoreChange);
        window.removeEventListener('storage', this.onStorage);
        if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.onMediaChange);
      }

      get limit() {
        return this.mobileQuery.matches
          ? Number(this.dataset.maxProductsMobile) || 2
          : Number(this.dataset.maxProducts) || 3;
      }

      render() {
        const items = CompareStore.read().slice(0, this.limit);
        const limit = this.limit;

        this.hidden = items.length === 0;
        this.style.setProperty('--compare-slots', String(limit));
        this.slots.textContent = '';

        items.forEach((item) => {
          const node = this.slotTemplate.content.cloneNode(true);
          const slot = node.querySelector('[data-compare-slot]');
          slot.dataset.handle = item.handle;
          slot.querySelector('[data-compare-slot-title]').textContent = item.title || '';
          const image = slot.querySelector('[data-compare-slot-image]');
          if (item.image) {
            image.src = item.image;
            image.alt = '';
          } else {
            image.remove();
          }
          this.slots.appendChild(node);
        });

        for (let index = items.length; index < limit; index += 1) {
          this.slots.appendChild(this.emptyTemplate.content.cloneNode(true));
        }

        const ready = items.length >= 2;
        this.cta.setAttribute('aria-disabled', String(!ready));

        const target = new URL(this.dataset.compareUrl, window.location.origin);
        if (ready) target.searchParams.set('products', items.map((item) => item.handle).join(','));
        this.cta.href = `${target.pathname}${target.search}`;

        document.documentElement.style.setProperty('--compare-bar-height', `${this.offsetHeight}px`);
      }
    }
  );
}

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-compare-toggle]');
  if (!input) return;

  const bar = document.querySelector('[data-compare-bar]');
  const limit = bar ? bar.limit : 3;

  if (!input.checked) {
    CompareStore.remove(input.dataset.compareHandle);
    return;
  }

  const added = CompareStore.add(
    {
      handle: input.dataset.compareHandle,
      title: input.dataset.compareTitle,
      image: input.dataset.compareImage,
    },
    limit
  );

  if (added === null) input.checked = false;
});

function syncCompareToggles() {
  const selected = CompareStore.read().map((item) => item.handle);
  const bar = document.querySelector('[data-compare-bar]');
  const limit = bar ? bar.limit : 3;
  const full = selected.length >= limit;

  document.querySelectorAll('[data-compare-toggle]').forEach((input) => {
    const isSelected = selected.includes(input.dataset.compareHandle);
    input.checked = isSelected;
    input.disabled = full && !isSelected;
  });
}

document.addEventListener(COMPARE_EVENT, syncCompareToggles);
document.addEventListener('DOMContentLoaded', syncCompareToggles);

if (document.readyState !== 'loading') syncCompareToggles();
